import { forwardRef, InputHTMLAttributes, useState, useId } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, EyeOff, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/cn';

interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  error?: string;
  hint?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'glass';
}

const sizeStyles = {
  sm: 'h-9 text-sm px-3',
  md: 'h-11 text-sm px-4',
  lg: 'h-12 text-base px-5',
};

const labelSizeStyles = {
  sm: 'text-xs',
  md: 'text-sm',
  lg: 'text-sm',
};

/**
 * Input - Modern input component with floating label
 * 
 * Features:
 * - Floating label animation
 * - Glass morphism styling
 * - Password visibility toggle
 * - Error/hint states with animation
 * - Left/right icon support
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      label,
      error,
      hint,
      leftIcon,
      rightIcon,
      size = 'md',
      variant = 'default',
      type = 'text',
      disabled,
      id: propId,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const id = propId || generatedId;
    const [isFocused, setIsFocused] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    
    const hasValue = props.value !== undefined && props.value !== '';
    const isPassword = type === 'password';
    const inputType = isPassword && showPassword ? 'text' : type;

    const variantStyles = {
      default: cn(
        'bg-navy-800/80 border-navy-600/50',
        'hover:border-navy-500/60',
        'focus:border-ocean-cyan/60 focus:bg-navy-800',
        error && 'border-signal-error/50 focus:border-signal-error/70'
      ),
      glass: cn(
        'bg-white/[0.03] backdrop-blur-xl border-white/[0.08]',
        'hover:border-white/[0.15]',
        'focus:border-ocean-cyan/40 focus:bg-white/[0.05]',
        error && 'border-signal-error/30 focus:border-signal-error/50'
      ),
    };

    return (
      <div className={cn('relative', className)}>
        {/* Label */}
        <AnimatePresence>
          {label && (
            <motion.label
              htmlFor={id}
              initial={false}
              animate={{
                y: isFocused || hasValue ? -24 : 0,
                scale: isFocused || hasValue ? 0.85 : 1,
                x: isFocused || hasValue ? -4 : leftIcon ? 32 : 0,
              }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className={cn(
                'absolute left-4 top-1/2 -translate-y-1/2',
                'origin-left pointer-events-none',
                'transition-colors duration-200',
                labelSizeStyles[size],
                isFocused ? 'text-ocean-cyan' : 'text-gray-400',
                error && (isFocused || hasValue) && 'text-signal-error',
                disabled && 'opacity-50'
              )}
            >
              {label}
            </motion.label>
          )}
        </AnimatePresence>

        {/* Input Container */}
        <div className="relative">
          {/* Left Icon */}
          {leftIcon && (
            <div className={cn(
              'absolute left-4 top-1/2 -translate-y-1/2',
              'text-gray-400',
              isFocused && 'text-ocean-cyan',
              error && 'text-signal-error'
            )}>
              {leftIcon}
            </div>
          )}

          {/* Input */}
          <input
            ref={ref}
            id={id}
            type={inputType}
            disabled={disabled}
            onFocus={(e) => {
              setIsFocused(true);
              props.onFocus?.(e);
            }}
            onBlur={(e) => {
              setIsFocused(false);
              props.onBlur?.(e);
            }}
            className={cn(
              'w-full rounded-xl border',
              'text-white placeholder-transparent',
              'transition-all duration-200',
              'focus:outline-none focus:ring-2 focus:ring-ocean-cyan/20',
              sizeStyles[size],
              variantStyles[variant],
              leftIcon && 'pl-11',
              (rightIcon || isPassword) && 'pr-11',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
            {...props}
          />

          {/* Right Icon / Password Toggle */}
          <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-2">
            {isPassword && (
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="text-gray-400 hover:text-gray-300 transition-colors"
                tabIndex={-1}
              >
                {showPassword ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            )}
            {rightIcon && !isPassword && (
              <span className={cn(
                'text-gray-400',
                isFocused && 'text-ocean-cyan'
              )}>
                {rightIcon}
              </span>
            )}
          </div>
        </div>

        {/* Error / Hint */}
        <AnimatePresence>
          {(error || hint) && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
              className={cn(
                'flex items-center gap-1.5 mt-2 text-xs',
                error ? 'text-signal-error' : 'text-gray-400'
              )}
            >
              {error && <AlertCircle className="w-3.5 h-3.5" />}
              <span>{error || hint}</span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;

